import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AssignEmployersMode } from '@core/enums/assign-employers-mode.enum';
import { StatusChips } from '@core/models';
import { AssignedEmployersDialog } from '@core/models/shared/assigned-employers-dialog.model';
import { appRoutePaths } from '@core/routes/route-paths.constants';
import { ConstantsService, LayoutService } from '@core/services';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { indicate } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { TemplateDetails } from '@core/models/case-communication/template-details.model';
import { ActionItemsTemplateDetails, AssignedEmployer } from '@core/models/action-item-templates/action-item-template-details.model';
import { ActionItemDetails, ActionItemEmployer } from '@core/models/action-item-templates/action-item-details.model';
import { TemplateDetailsStoreService } from '@core/services/template-details-store.service';
import { ActionItemsTemplateDetailsStoreService } from '@core/services/action-item-template-details-store.service';
import { ActionItemDetailsStoreService } from '@core/services/action-item-details-store.service';
import { EditAssignedEmployersComponent } from '@modules/dialogs/edit-assigned-employers/edit-assigned-employers.component';
import { TemplateEmployer } from '@core/models/case-communication/template-item.model';
import { LeaveReasonsService } from '@core/services/leave-reasons.service';
import { LeaveReasonDetails } from '@core/models/leave-reasons/leave-reason-details.model';
import { LeaveReasonDetailsStoreService } from '@core/services/leave-reason-deatils-store.service';

@Component({
    selector: 'app-assigned-employers',
    templateUrl: './assigned-employers.component.html',
    styleUrls: ['./assigned-employers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssignedEmployersComponent implements OnInit {
  static readonly componentName = 'AssignedEmployersComponent';
  
  @Input() mode: AssignEmployersMode;
  @Input() canEditEmployers: boolean;
  @Input() id: number;
  @Input() employers: TemplateEmployer[] | AssignedEmployer[] | ActionItemEmployer[];
  @Input() sysText: any;
  @Input() unsavedChangesSysText: any;
  @Output() isEditing = new EventEmitter<boolean>();

  public isMobile$: Observable<boolean>;
  public panelOpenState: boolean = true;
  public chipStyles: StatusChips;
  public isLoading$: Subject<boolean> = new Subject<boolean>();
  public assignedEmployerType: AssignedEmployer;
  public routes: typeof appRoutePaths = appRoutePaths;

  constructor(
    private constants: ConstantsService,
    private dialog: MatDialog,
    private layoutService: LayoutService,
    // email templates 
    private caseCommunicationService: CaseCommunicationService,
    private emailTemplateStore: TemplateDetailsStoreService,
    // action items/templates
    private actionItemsTemplatesService: ActionItemsTemplatesService,
    private actionItemsTemplateStore: ActionItemsTemplateDetailsStoreService,
    private actionItemStore: ActionItemDetailsStoreService,
    // leave reasons
    private leaveReasonService: LeaveReasonsService,
    private leaveReasonStore: LeaveReasonDetailsStoreService
  ) { }

  ngOnInit(): void {
    this.chipStyles = this.constants.STAT_CHIP_STYLES;
    this.isMobile$ = this.layoutService.isMobile$();
  }

  public editAssignedEmployers(): void {
    let dialogData$: Observable<AssignedEmployersDialog>;

    if (this.mode === AssignEmployersMode.EmailTemplate) {
      dialogData$ = this.caseCommunicationService.getEditTemplateAssignedEmployersDialog(this.id);
    }
    else if (this.mode === AssignEmployersMode.ActionItemsTemplate) {
      dialogData$ = this.actionItemsTemplatesService.getEditTemplateAssignedEmployersDialog(this.id);
    }
    else if (this.mode === AssignEmployersMode.ActionItem) {
      dialogData$ = this.actionItemsTemplatesService.getEditActionItemAssignedEmployersDialog(this.id);
    }
    else if (this.mode === AssignEmployersMode.LeaveReason) {
      dialogData$ = this.leaveReasonService.getEditLeaveReasonAssignedEmployersDialog(this.id);
    }

    dialogData$.pipe(indicate(this.isLoading$))
      .subscribe(res => {
        const dialogConfig: MatDialogConfig = {
          id: EditAssignedEmployersComponent.componentName,
          width:'550px',
          panelClass: "mat-dialog-container-mobileWidth",
          data: { 
            mode: this.mode,
            id: this.id,
            employers: this.employers,
            employerOptions: res.employers,
            showDefaults: res.showDefaults,
            sysText: this.sysText,
            unsavedChangesSysText: this.unsavedChangesSysText
          }};
        
        const dialogRef = this.dialog.open(EditAssignedEmployersComponent, dialogConfig);
        dialogRef.componentInstance.isEditing.subscribe((res) => {
          this.isEditing.emit(res);
        });
        dialogRef.afterClosed().subscribe((res) => {
          if (res) {
            this.updateData(res);
          }
          this.isEditing.emit(false);
        })
      });
  }

  private updateData(data: 
      TemplateDetails | 
      ActionItemsTemplateDetails | 
      ActionItemDetails | 
      LeaveReasonDetails): void {
    if (this.mode === AssignEmployersMode.EmailTemplate) {
      this.emailTemplateStore.templateDetails = data as TemplateDetails;
    } 
    else if (this.mode === AssignEmployersMode.ActionItemsTemplate) {
      this.actionItemsTemplateStore.templateDetails = data as ActionItemsTemplateDetails;
    } 
    else if (this.mode === AssignEmployersMode.ActionItem) {
      this.actionItemStore.actionItemDetails = data as ActionItemDetails;
    }
    else if (this.mode === AssignEmployersMode.LeaveReason) {
      this.leaveReasonStore.leaveReasonDetails = data as LeaveReasonDetails;
    }
  }
}
